import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { path } from 'ramda'
import { colors } from '@/styles'
import { Label, ParagraphMedium } from '@/components/text'
import { Box } from '@/components/structure'
import { Arrow } from '@/components/animations'
import StatusBadge from '@/components/status-badge'
import { flags } from '@/flags'
import { useToaster } from '@/models/toaster/hooks'
import { useAddendumModal } from './context'
import CustomList from './custom-list'
import EditAddendum from './edit-addendum'
import ModalBase from './modal-base'
import StandardList from './standard-list'
import { CardWrapper } from './buttons'
import showAddendumLimitModal from '../addendum-limit-reached'

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px 0;
`

const CardTitleWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 0 12px;
  svg {
    margin-top: 3px;
    flex-shrink: 0;
  }
`

const Card = ({ title, description, onClick, disabled, children }) => {
  const [active, setActive] = useState(false)
  const enter = () => setActive(true)
  const leave = () => setActive(false)
  const handlers = !disabled
    ? {
        onMouseLeave: leave,
        onMouseEnter: enter,
        onMouseDown: enter,
        onMouseUp: leave,
        onFocus: enter,
        onBlur: leave
      }
    : {}
  return (
    <CardWrapper onClick={onClick} disabled={disabled} {...handlers}>
      {children}
      <CardTitleWrapper>
        <Label type="large" as="span">
          {title}
        </Label>
        <Arrow isOpen={active} align="middle" />
      </CardTitleWrapper>
      <Box color={colors.grey56}>
        <ParagraphMedium>{description}</ParagraphMedium>
      </Box>
    </CardWrapper>
  )
}

const curr = 'complete_contract.add_addendum'
const next = 'send_contract'

const Home = () => {
  const { t } = useTranslation()
  const { close, addStep, addContractAddendums, planType } = useAddendumModal()
  const enableBlocks = path(['features', 'blocks'])(flags)

  const toaster = useToaster()
  const state = toaster?.tasks?.complete_contract?.steps?.add_addendum?.state
  const updateState = toaster.updateState
  useEffect(() => {
    if (state === 'complete') return
    updateState(curr, {
      step: {
        state: 'complete'
      },
      next: [
        {
          key: next,
          step: { state: 'incomplete' }
        }
      ]
    })
  }, [state, updateState])

  const handleAddOneTimeAddendum = addendum => {
    addContractAddendums([addendum])
    close()
  }

  const standardList = {
    component: StandardList
  }

  const customList = {
    component: CustomList
  }

  const createOneTime = {
    component: EditAddendum,
    addendumType: 'one_time',
    isEditing: true,
    titleText: t(
      'l.contract.edit.create_one_time_addendum_modal.title',
      'Create one-time addendum'
    ),
    footnote: t(
      'l.contract.edit.edit_addendum_one_time_info.text',
      'Please note: This addendum will not be saved for future use. Only company templates are reusable. '
    ),
    buttonText: t('l.contract.edit.create_addendum.button', 'Create Addendum'),
    onSubmit: handleAddOneTimeAddendum
  }

  const cards = [
    {
      title: t('l.contract.edit.addendums_by_liv.title', 'Addendums by liv.rent'),
      description: t(
        'l.contract.edit.addendums_by_liv.text',
        'Standard addendums that we have prepared for your convenience.'
      ),
      onClick: () => {
        enableBlocks && planType === 'personal'
          ? showAddendumLimitModal({ planType: planType })
          : addStep(standardList)
      }
    },
    {
      title: t('l.contract.edit.addendums_by_company.title', 'Addendums by your company'),
      description: t(
        'l.contract.edit.addendums_by_company.text',
        'Addendums created by your company admins. Admins may also make changes.'
      ),
      onClick: () => {
        enableBlocks && (planType === 'family' || planType === 'personal')
          ? showAddendumLimitModal({ planType: planType })
          : addStep(customList)
      },
      children: (
        <Box pbottom={8}>
          <StatusBadge type="info" title={t('b.auth.create.business.label', 'Business')} />
        </Box>
      )
    },
    {
      title: t('l.contract.edit.addendums_one_time.title', 'Create one-time addendum'),
      description: t(
        'l.contract.edit.addendums_one_time.text',
        'Create for your personal use. This won’t be saved or shared with anyone.'
      ),
      onClick: () => {
        enableBlocks && planType === 'personal'
          ? showAddendumLimitModal({ planType: planType })
          : addStep(createOneTime)
      }
    }
  ]

  return (
    <ModalBase
      rightWidget={null}
      title={t(
        'l.contract.edit.add_addendum_modal.title',
        'Which addendum templates would you like to add?'
      )}
    >
      <ContentInner>
        <CardsWrapper>
          {cards.map(({ key, ...rest }) => (
            <Card type="button" key={key} {...rest} />
          ))}
        </CardsWrapper>
      </ContentInner>
    </ModalBase>
  )
}

export default Home
